import { isStructuredValue } from '@aldebaran/common/util/structuredValue';
import { ResponseAnnuaireSanteField } from './ResponseAnnuaireSanteField';
import { Value, ViewableQuestion, ViewableResponse } from '../types/global';
import { useState, useEffect } from 'react';

import { ResponseButton } from './ResponseButton';
import { ResponseDateField } from './ResponseDateField';
import { ResponseDateTimeField } from './ResponseDateTimeField';
import { ResponseDOBField } from './ResponseDOBField';
import { ResponseNumberField } from './ResponseNumberField';
import { ResponseOtherField } from './ResponseOtherField';
import { ResponseTextField } from './ResponseTextField';
import { ResponseTimeField } from './ResponseTimeField';
import { ResponseDurationField } from './ResponseDurationField';
import { ResponseSliderField } from './ResponseSliderField';
import { ResponseFileUploadField } from './ResponseFileUploadField';
import { ResponseDocumentAcknowledgementField } from './ResponseDocumentAcknowledgementField';
interface ResponseProps {
  response: ViewableResponse;
  onChange: (value: Value) => void;
  value: Value;
  questionLabel: ViewableQuestion['label'];
  questionComment: ViewableQuestion['comment'];
  questionAlwaysShowLabel: ViewableQuestion['alwaysShowLabel'];
  disabled?: boolean;
  multiselect?: boolean;
}

export const Response = ({
  response,
  onChange,
  value,
  multiselect,
  questionLabel,
  questionComment,
  disabled,
  questionAlwaysShowLabel,
}: ResponseProps) => {
  const alwaysShowLabel = response.alwaysShowLabel ?? questionAlwaysShowLabel;

  // For text fields, use local state to prevent interference with composition
  // This solves the 'é' issue on Mac OS Keyboards
  const [localTextValue, setLocalTextValue] = useState(String(value || ''));

  useEffect(() => {
    setLocalTextValue(String(value || ''));
  }, [value]);

  const handleTextChange = (newValue: string) => {
    const previousValue = localTextValue;
    setLocalTextValue(newValue);

    // Only call onChange once when transitioning between empty and non-empty to update the CTA from "Passer" to "Suivant"
    const wasEmpty = previousValue.trim() === '';
    const isEmpty = newValue.trim() === '';

    if (wasEmpty !== isEmpty) {
      onChange(newValue);
    }
  };

  const handleTextBlur = () => {
    if (localTextValue !== String(value || '')) {
      onChange(localTextValue);
    }
  };

  switch (response.format) {
    case 'INTEGER':
      return (
        <ResponseNumberField
          label={response.label}
          value={value?.toString()}
          onChange={onChange}
          unit={response.unit?.plural}
          min={response.minimum}
          max={response.maximum}
          disabled={disabled}
          integer
          alwaysShowLabel={alwaysShowLabel}
        />
      );
    case 'Valeur':
    case 'NUMBER':
    case 'DECIMAL':
      return (
        <ResponseNumberField
          label={response.label}
          value={value?.toString()}
          onChange={onChange}
          unit={response.unit?.plural}
          min={response.minimum}
          max={response.maximum}
          disabled={disabled}
          alwaysShowLabel={alwaysShowLabel}
          integer={false}
          increment={response.increment}
        />
      );
    case 'SLIDER':
      return (
        <ResponseSliderField
          value={value as number}
          onChange={onChange}
          min={response.minimum}
          max={response.maximum}
          increment={response.increment}
          disabled={disabled}
          label={response.label}
        />
      );
    case 'DATETIME':
      return (
        <ResponseDateTimeField
          label={response.label}
          value={(value as number) ?? undefined}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'DATE':
      return (
        <ResponseDateField
          label={response.label}
          value={(value as number) ?? undefined}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'TIME':
      return (
        <ResponseTimeField
          label={response.label}
          questionLabel={questionLabel}
          value={(value as number) ?? undefined}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'DURATION':
      return (
        <ResponseDurationField
          label={response.label}
          value={(value as number) ?? undefined}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'DATE_OF_BIRTH':
      return (
        <ResponseDOBField
          value={(value as number) ?? undefined}
          onBlur={onChange}
          disabled={disabled}
        />
      );
    case 'Field':
    case 'TEXT':
      return (
        <ResponseTextField
          label={response.label}
          value={localTextValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          alwaysShowLabel={alwaysShowLabel}
        />
      );
    case 'multiLineText':
    case 'MULTILINE_TEXT':
      return (
        <ResponseTextField
          label={response.label}
          value={localTextValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          alwaysShowLabel={alwaysShowLabel}
          multiline
        />
      );
    case 'FILE_UPLOAD':
      return (
        <ResponseFileUploadField
          response={response}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'ANNUAIRE_SANTE_AUTOCOMPLETE':
      return (
        <ResponseAnnuaireSanteField
          response={response}
          value={isStructuredValue(value) ? value : null}
          onChange={onChange}
          questionLabel={questionLabel}
          questionComment={questionComment}
          disabled={disabled}
        />
      );
    case 'DOCUMENT_ACKNOWLEDGEMENT':
      return (
        <ResponseDocumentAcknowledgementField
          response={response}
          value={Boolean(value)}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case 'OTHER_TEXT':
      return (
        <ResponseOtherField
          questionLabel={questionLabel}
          label={response.label}
          value={value?.toString()}
          onChange={onChange}
          multiselect={multiselect}
        />
      );
    default:
      return (
        <div className="flex items-end">
          <ResponseButton
            comment={response.comment}
            selected={Boolean(value)}
            onChange={onChange}
            label={response.label ?? ''}
            multiselect={multiselect}
          />
        </div>
      );
  }
};
